import { Dexie } from 'dexie';
import { Base64 } from 'js-base64';
import pako from 'pako';
import AsyncLock from 'async-lock';

import { InMemoryCache } from './inMemoryCache';

const DB_VERSION = 6;
const COOLDOWN_TIME = 1000; // 高頻度のサーバアクセスを防ぐために、サーバアクセスをスキップする時間（ms）

interface BaseDbStructure {
  db: Dexie;
  entries: Dexie.Table;
  meta: Dexie.Table;
}

const initDbAndTables = (tableName: string, primaryKeyName: string, secondaryKeyName: string, version = DB_VERSION): BaseDbStructure => {
  const db = new Dexie(tableName);
  db.version(version).stores({
    entries: `[${primaryKeyName}+${secondaryKeyName}]`,
    meta: 'name',
  });
  return {
    db,
    entries: (db as any).entries,
    meta: (db as any).meta,
  };
};

/**
 *
 */
class CacheManage {
  protected readonly db: BaseDbStructure;
  protected readonly initializer?: (tableName: string, primaryKeyName: string, secondaryKeyName: string) => any;
  protected readonly tableName: string;
  protected readonly _primaryKeyName: string;
  protected readonly _secondaryKeyName: any;
  protected gsiKeyName = '';
  protected dataCache: InMemoryCache;
  protected lock = new AsyncLock();
  protected recentAccess: { [key: string]: number } = {}; // 直近のgetByTime要求発生時刻。１秒以内に何度も同じprimaryKeyに関する要求がきても、サーバには問い合わせ内容にする
  protected latest: { [key: string]: number } = {};
  protected lastReceivedForAvoidDup: { [key: string]: (string | number)[] } = {}; // getByTime()は直前に受信したメッセージの最終時刻と同時刻のものを次も受信する仕様なので、アプリ側にそれを伝えないようにするためにメモを置いておく
  protected toleranceOfGetByTime = 0;
  protected removeLeadTime = 604800; // 7*86400s
  protected deleteLocalEntryDuration = 0; // 負の値の場合に限りremoveAtが設定されたら直ちにローカルのエントリを削除する。0以上であれば設定されているremoveAtの時刻からdeleteLocalEntryDurationの時間だけ前になったら削除する

  public createAdapter = async (value: any) => {
    value.updatedAt = new Date().getTime();
    return value;
  }; // put()時（データ新規登録）のサーバへのアクセス関数で上書きする
  public updateAdapter = async (value: any) => {
    value.updatedAt = new Date().getTime();
    return value;
  }; // put()時（データ更新および削除）のサーバへのアクセス関数で上書きする
  public deleteAdapter = async (value: any): Promise<any> => {
    value.deleted = true;
    value.updatedAt = new Date().getTime();
    return value;
  }; // delete()時（データ削除）のサーバへのアクセス関数で上書きする
  public readAdapter2 = async (primaryKey: string, timestamp = 0, filter: { [key: string]: any } | undefined): Promise<any> => [1]; // readAdapterで返却されるデータを圧縮するバージョン（今後これを主流にする９
  public readAllAdapter = async (): Promise<any> => []; // scanAll()時のサーバへのアクセス関数で上書きする
  public bulkCreateAdapter = async (valuesB64: string) => ''; // put()時（データ新規登録）のサーバへのアクセス関数で上書きする
  public bulkUpdateAdapter = async (valuesB64: string) => ''; // put()時（データ新規登録）のサーバへのアクセス関数で上書きする
  public flushForTestingAdapter = async (): Promise<any> => '';

  public static init = async (tableName: string, primaryKeyName: string, secondaryKeyName: any, email?: string): Promise<CacheManage> => {
    let cache = new CacheManage(tableName, primaryKeyName, secondaryKeyName, initDbAndTables);
    const db = cache.getDb();
    try {
      await db.db.open();
    } catch (d) {
      await db.db.delete(); // 何かおかしいので、一度キャッシュを削除して作り直す
      cache = new CacheManage(tableName, primaryKeyName, secondaryKeyName, initDbAndTables);
    }
    await CacheManage._init(cache, tableName, primaryKeyName, secondaryKeyName, email);
    return cache;
  };

  /**
   * あらゆるクラスで利用する共通の初期化処理（メタ情報を書き込む）
   * @param cache
   * @param tableName
   * @param primaryKeyName
   * @param secondaryKeyName
   * @param email
   */
  protected static _init = async <T extends CacheManage>(
    cache: T,
    tableName: string,
    primaryKeyName: string,
    secondaryKeyName: any,
    email?: string
  ) => {
    const db = cache.getDb();
    const latest = await db.meta.get('latest');
    if (typeof latest === 'undefined') {
      await db.meta.put({ name: 'latest', value: {} });
    } else {
      cache.latest = latest.value;
    }

    await db.meta.put({ name: 'tableName', tableName });
    await db.meta.put({ name: 'primaryKey', primaryKeyName });
    await db.meta.put({ name: 'secondaryKey', primaryKeyName });
  };

  /**
   *
   */
  get primaryKeyName(): string {
    return this._primaryKeyName;
  }
  /**
   *
   */
  get secondaryKeyName(): string {
    return this._secondaryKeyName;
  }

  /**
   *
   * @param gsi
   */
  public setGsi(gsi: string) {
    this.gsiKeyName = gsi;
  }

  public getDb = () => this.db;
  public setTolerance = (val: number) => (this.toleranceOfGetByTime = val);
  public setRemoveLeadTime = (val: number) => (this.removeLeadTime = val);
  public setDeleteLocalEntryDuration = (val: number) => (this.deleteLocalEntryDuration = val);
  protected shouldBeRemoved = (record: any): boolean => {
    // EXP: すでに消えていてアプリケーション側に返答すべきでない場合はtrueを返す
    if (!record.removeAt || record.removeAt < 0) return false;
    if (this.deleteLocalEntryDuration < 0) return true;
    // EXP 現在時刻が、削除予定時刻-deleteLocalEntryDurationを超えている場合はtrue（つまり、アプリには返答しない）
    return record.removeAt - this.deleteLocalEntryDuration < Math.floor(new Date().getTime() / 1000); // removeAtはDynamoDBのTTLになるので秒単位
  };

  /**
   * アダプターをセットする
   * @param object.createAdapter
   * @param object.updateAdapter
   * @param object.deleteAdapter
   * @param object.readAdapter
   * @param object.readAdapter2
   * @param object.readAllAdapter
   * @param object.bulkCreateAdapter
   * @param object.bulkUpdateAdapter
   * @param object.flushForTestingAdapter
   */
  public setup({
    createAdapter,
    updateAdapter,
    deleteAdapter,
    readAdapter2,
    readAllAdapter,
    bulkCreateAdapter,
    bulkUpdateAdapter,
    flushForTestingAdapter,
  }: any) {
    if (createAdapter) this.createAdapter = createAdapter;
    if (updateAdapter) this.updateAdapter = updateAdapter;
    if (deleteAdapter) this.deleteAdapter = deleteAdapter;
    if (readAdapter2) this.readAdapter2 = readAdapter2;
    if (readAllAdapter) this.readAllAdapter = readAllAdapter;
    if (bulkCreateAdapter) this.bulkCreateAdapter = bulkCreateAdapter;
    if (bulkUpdateAdapter) this.bulkUpdateAdapter = bulkUpdateAdapter;
    if (flushForTestingAdapter) this.flushForTestingAdapter = flushForTestingAdapter;
  }

  /**
   * コンストラクタ
   * @param tableName
   * @param primaryKeyName
   * @param secondaryKeyName
   * @param initializer
   * @param version
   */
  constructor(
    tableName: string,
    primaryKeyName: string,
    secondaryKeyName: string,
    initializer?: (tableName: string, primaryKeyName: string, secondaryKeyName: string, version?: number) => any,
    version = DB_VERSION
  ) {
    this.tableName = tableName;
    this._primaryKeyName = primaryKeyName;
    this._secondaryKeyName = secondaryKeyName;
    this.dataCache = new InMemoryCache(primaryKeyName, secondaryKeyName, this.getFromIndexedDb);
    this.initializer = initializer;
    if (!initializer || tableName === '') {
      this.db = { db: {} as Dexie, entries: {} as Dexie.Table, meta: {} as Dexie.Table };
      return;
    }
    this.db = initializer(tableName, primaryKeyName, secondaryKeyName, version); // this.tableをメインで使うが、サーバとの通信を避けるためのキャッシュとして使う。
  }

  /**
   * primaryKeyを指定して該当する全てのレコードをIndexedDbから取得するユーティリティ
   * @param primaryKeyValue
   * @protected
   */
  public getFromIndexedDb = async (primaryKeyValue: string): Promise<any> =>
    this.db.entries
      .where([this.primaryKeyName, this.secondaryKeyName])
      .between([primaryKeyValue, Dexie.minKey], [primaryKeyValue, Dexie.maxKey], true, true)
      .toArray();

  /**
   * IndexedDBに書き込む（引数はarrayでも単体でもOK）
   * @param dat
   * @protected
   */
  protected async putInIndexedDb(dat: any) {
    if (Array.isArray(dat)) {
      await this.db.entries.bulkPut(dat);
    } else {
      await this.db.entries.put(dat);
    }
  }

  /**
   * IndexedDBとメモリの両方にデータを書き込む
   * @param datToRegister
   * @protected
   */
  protected async putData(datToRegister: any) {
    await Promise.all([
      this.dataCache.updateInMemory(datToRegister),
      this.putInIndexedDb(datToRegister),
      this.db.meta.put({ name: 'latest', value: this.latest }),
    ]);
  }

  protected async getEntry(primaryKeyValue: string, secondaryKeyValue: string): Promise<any> {
    return await this.dataCache.getInMemory(primaryKeyValue, secondaryKeyValue);
  }

  protected formatCheck(value: any) {
    if (!(this._primaryKeyName in value)) {
      throw Error('invalid primary key');
    }
    if (!('updatedAt' in value)) {
      throw Error('invalid value format. updatedAt is mandatory');
    }
  }

  /**
   * 最新時刻を巻き戻す（不具合が起こったときのため）
   * @param primaryKeyValue
   * @param timeToRewind
   */
  public async rewindTimestamp(primaryKeyValue: string, timeToRewind: number) {
    if (!this.latest[primaryKeyValue]) return;
    this.latest[primaryKeyValue] = this.latest[primaryKeyValue] - timeToRewind > 0 ? this.latest[primaryKeyValue] - timeToRewind : 0;
    await this.db.meta.put({ name: 'latest', value: this.latest });
  }

  /**
   * 最新時刻を強制設定する
   * @param primaryKeyValue
   * @param timestampMillisec
   */
  public async setLatestTimestamp(primaryKeyValue: string, timestampMillisec: number) {
    if (!this.latest[primaryKeyValue]) return;
    this.latest[primaryKeyValue] = timestampMillisec;
    await this.db.meta.put({ name: 'latest', value: this.latest });
  }

  /**
   *
   * @param primaryKeyValue
   */
  public getLatestTimestamp(primaryKeyValue: string) {
    return this.latest[primaryKeyValue];
  }

  /**
   * 複数のレコードを一括で登録する
   * @param records {any}
   */
  public async bulkPut<T extends Array<object>>(records: T) {
    const entriesToCreate: any[] = [];
    const entriesToUpdate: any[] = [];
    let primaryKeyList: string[] = [];
    for (const val of records) {
      primaryKeyList.push(val[this._primaryKeyName]);

      const entry = await this.getEntry(val[this._primaryKeyName], val[this._secondaryKeyName]);
      if (!entry) {
        entriesToCreate.push(val);
      } else {
        entriesToUpdate.push(val);
      }
    }
    primaryKeyList = [...new Set(primaryKeyList)];

    // OBJ: サーバに登録する
    let responses: any[] = [];
    if (entriesToCreate.length > 0) {
      const toUpload = Base64.fromUint8Array(pako.deflate(JSON.stringify(entriesToCreate)));
      const res: string = await this.bulkCreateAdapter(toUpload);
      if (res) {
        JSON.parse(pako.inflate(Base64.toUint8Array(res), { to: 'string' })).forEach((r) => {
          this.updateWithResponse(entriesToCreate, r);
        });
        responses = entriesToCreate;
      }
    }
    if (entriesToUpdate.length > 0) {
      const toUpload = Base64.fromUint8Array(pako.deflate(JSON.stringify(entriesToUpdate)));
      const res = await this.bulkUpdateAdapter(toUpload);
      if (res) {
        JSON.parse(pako.inflate(Base64.toUint8Array(res), { to: 'string' })).forEach((r) => {
          this.updateWithResponse(entriesToUpdate, r);
        });
        responses = [...responses, ...entriesToUpdate];
      }
    }

    // EXP: IndexedDBおよびメモリキャッシュに登録する
    responses.forEach((r) => {
      if (!this.latest[r[this._primaryKeyName]] || this.latest[r[this._primaryKeyName]] < r.updatedAt) {
        this.latest[r[this._primaryKeyName]] = r.updatedAt;
      }
    });
    const promises: any = [];
    primaryKeyList.forEach((k) => {
      const res = responses.filter((r) => r[this._primaryKeyName] === k);
      promises.push(this.putData(res));
    });
    await Promise.all(promises);

    return responses;
  }

  private updateWithResponse = (entries: any[], resp: any) => {
    const r = entries.find(
      (rec) => rec[this._primaryKeyName] === resp[this._primaryKeyName] && rec[this._secondaryKeyName] === resp[this._secondaryKeyName]
    );
    if (!r) return;
    r.updatedAt = resp.updatedAt;
  };

  /**
   * エントリを登録または更新する。サーバにも登録・更新する
   * @param value
   */
  public async put(value: any) {
    const entry: any = await this.getEntry(value[this._primaryKeyName], value[this._secondaryKeyName]);

    let response: any;
    if (!entry) {
      // EXP: 新規登録（最初にDBに登録し、updatedAtをサーバに埋めてもらう）
      response = await this.createAdapter(value);
    } else {
      // OBJ: 削除したものと同じHashkey,Rangekeyの情報を登録しようとした場合は、削除フラグやremoveAtを取り消す必要がある
      if (entry.removeAt && entry.removeAt !== -1 && (!value.removeAt || value.removeAt === -1)) {
        value.removeAt = -1;
        if (entry.deleted) value.deleted = false;
      } else if (entry.deleted && !value.deleted) {
        value.deleted = false;
      }

      // EXP: 更新（最初にDBに登録し、updatedAtをサーバに埋めてもらう）
      response = await this.updateAdapter(value);
    }
    if (!response) {
      console.error(
        'サーバに正しく接続できていない、サーバで障害が発生している、またはローカルキャッシュに存在しているのにサーバ上に存在しないエントリにアクセスしようとした可能性があります'
      );
      return;
    }
    value.updatedAt = response.updatedAt;

    // EXP: このif文はそれ以外、つまりIndexedDBに保存するレコードの処理
    const primaryKeyValue = value[this.primaryKeyName];
    if (!this.latest[primaryKeyValue] || this.latest[primaryKeyValue] < value.updatedAt) {
      this.latest[primaryKeyValue] = value.updatedAt;
    }

    // OBJ: IndexedDBに値を書き込む
    await this.putData(value);
    return value;
  }

  /**
   *
   * @param primaryKeyValue
   * @param secondaryKeyValue
   * @param info
   */
  public async removeWithoutDelFlag(primaryKeyValue: string, secondaryKeyValue?: any, info = {}): Promise<boolean> {
    return this._remove(false, primaryKeyValue, secondaryKeyValue, info);
  }

  /**
   *
   * @param primaryKeyValue
   * @param secondaryKeyValue
   * @param info
   */
  public async remove(primaryKeyValue: string, secondaryKeyValue?: any, info = {}): Promise<boolean> {
    return this._remove(true, primaryKeyValue, secondaryKeyValue, info);
  }

  /**
   *
   * @param entries
   * @param withoutDelFlag
   */
  public async bulkRemove(entries: any[], withoutDelFlag = false) {
    const records: any = await Promise.all(entries.map((e) => this.getEntry(e[this._primaryKeyName], e[this._secondaryKeyName])));
    if (!records || records.length === 0) return;
    const removeAt = Math.floor(new Date().getTime() / 1000 + this.removeLeadTime);
    const recordToUpdate: any[] = records
      .filter((e) => e)
      .map((e) => {
        e.removeAt = removeAt;
        if (!withoutDelFlag) e.deleted = true;
        return e;
      });
    if (records.length === 0) return;
    await this.bulkPut(recordToUpdate);
  }

  /**
   * 削除したことを表す情報をレコードにセットする（他のクライアントにも削除が伝わるように）
   * @param withFlag
   * @param primaryKeyValue
   * @param secondaryKeyValue
   * @param info
   */
  private async _remove(withFlag: boolean, primaryKeyValue: string, secondaryKeyValue?: any, info = {}): Promise<boolean> {
    let entry = await this.getEntry(primaryKeyValue, secondaryKeyValue);
    if (!entry) return false;

    const removeAt = Math.floor(new Date().getTime() / 1000 + this.removeLeadTime);
    const recordToUpdate: any = { [this._primaryKeyName]: primaryKeyValue, removeAt, ...info };
    if (withFlag) recordToUpdate.deleted = true;
    recordToUpdate[this._secondaryKeyName] = secondaryKeyValue;

    const updatedPortion = await this.updateAdapter(recordToUpdate);
    if (!updatedPortion) return false;
    entry = { ...entry, ...recordToUpdate, ...updatedPortion };
    if (!this.latest[primaryKeyValue] || this.latest[primaryKeyValue] < entry.updatedAt) {
      this.latest[primaryKeyValue] = entry.updatedAt;
    }

    // OBJ: IndexedDBに値を書き込む
    await this.putData(entry);
    return true;
  }

  /**
   * サーバおよびローカルの情報を同時に削除する
   * @param primaryKeyValue
   * @param secondaryKeyValue
   */
  public async forceDelete(primaryKeyValue: string, secondaryKeyValue?: any): Promise<boolean> {
    const res = await this.getEntry(primaryKeyValue, secondaryKeyValue);
    if (!res) return false;
    await this.dataCache.deleteInMemory(primaryKeyValue, secondaryKeyValue); // ローカルの方は持ってても仕方ないので削除する
    await Promise.all([
      this.db.entries.delete([primaryKeyValue, secondaryKeyValue]),
      this.deleteAdapter({ [this._primaryKeyName]: primaryKeyValue, [this._secondaryKeyName]: secondaryKeyValue }),
    ]);
    return true;
  }

  /**
   * 指定されたprimaryKeyに紐づく全ての情報をローカルから削除する
   * @param primaryKeyValue
   * @protected
   */
  protected async bulkDeleteLocal(primaryKeyValue: string) {
    // EXP: in-memoryから削除する
    this.dataCache.deleteInMemory(primaryKeyValue);

    // EXP: IndexedDBから削除する
    const entries = await this.db.entries
      .where([this.primaryKeyName, this.secondaryKeyName])
      .between([primaryKeyValue, Dexie.minKey], [primaryKeyValue, Dexie.maxKey], true, true)
      .toArray();
    await this.db.entries.bulkDelete(entries.map((e: any) => [primaryKeyValue, e.dataId]));

    // EXP: latestのタイムスタンプを削除する
    if (this.latest[primaryKeyValue]) {
      delete this.latest[primaryKeyValue];
      await this.db.meta.put({ name: 'latest', value: this.latest });
    }
  }

  /**
   * 指定されたprimaryKeyに対するエントリ群を返答する。
   * サーバにアクセスせず、ローカルの情報だけを検索する。確実に同期していると言える場合にだけ利用すること
   * @param primaryKeyValue
   */
  public localGetAll(primaryKeyValue: string): any {
    return this.dataCache.getSortedArray(primaryKeyValue);
  }

  /**
   * 未取得のデータを取得する
   *
   * このオブジェクトが管理している最新の更新データより後に更新された情報をサーバからとってくる
   * @param primaryKeyValue
   * @param timestamp
   * @param filter
   */
  public async getNewRecordsFromServer(primaryKeyValue: string, timestamp: number, filter: { [key: string]: any } | undefined) {
    const ts = this.latest[primaryKeyValue] > this.toleranceOfGetByTime ? this.latest[primaryKeyValue] - this.toleranceOfGetByTime : 0;
    const retDatB64: string = await this.readAdapter2(primaryKeyValue, ts, filter); // サーバから時刻を指定してそれ以降に更新されたデータをとってくる(圧縮されている）
    if (!retDatB64) return [];
    let result: any[] = JSON.parse(pako.inflate(Base64.toUint8Array(retDatB64), { to: 'string' }));

    if (!result || result.length === 0) {
      if (this.latest[primaryKeyValue]) {
        this.latest[primaryKeyValue] += 1; // 何も更新がなかったということなので、とりあえず1ミリ秒進めておいて今後の重複受信を防ぐ
        await this.db.meta.put({ name: 'latest', value: this.latest });
        this.lastReceivedForAvoidDup[primaryKeyValue] = [];
      }
      return [];
    }

    let primaryKeys: string[] = result.map((item) => item[this.primaryKeyName]);
    primaryKeys = [...new Set(primaryKeys)];
    for (const primaryKeyValue of primaryKeys) {
      // EXP: 前回取得した最新のメッセージと同一時刻のメッセージを受信してしまうため、それを削除するためのメモを適用する
      if (this.lastReceivedForAvoidDup[primaryKeyValue] && this.lastReceivedForAvoidDup[primaryKeyValue].length > 0) {
        result = result.filter(
          (ent) =>
            !this.lastReceivedForAvoidDup[primaryKeyValue].includes(ent[this.secondaryKeyName]) ||
            this.lastReceivedForAvoidDup[primaryKeyValue][0] < ent.updatedAt
        );
      }

      let latestTimestamp = 0;
      result.forEach((ent: any) => {
        this.formatCheck(ent);
        if (ent.updatedAt > latestTimestamp) {
          latestTimestamp = ent.updatedAt;
          this.lastReceivedForAvoidDup[primaryKeyValue] = [latestTimestamp, ent[this.secondaryKeyName]]; // 1番目の要素に時刻を入れておく
        } else if (ent.updatedAt === latestTimestamp) {
          this.lastReceivedForAvoidDup[primaryKeyValue].push(ent[this.secondaryKeyName]);
        }
      });
      if (latestTimestamp > 0) this.latest[primaryKeyValue] = latestTimestamp;
    }

    if (result.length > 0) {
      await this.putData(result); // EXP: ローカルのメモリとIndexedDBに書き込む
    }
    return result;
  }

  /**
   * _getByTime() へのラッパー（フィルタなし）
   * @param primaryKeyValue
   * @param timestamp
   * @param withDeleted
   */
  public async getByTime(primaryKeyValue: string, timestamp = 0, withDeleted = false): Promise<any[]> {
    return await this._getByTime(primaryKeyValue, timestamp, undefined, withDeleted);
  }

  /**
   * getByTime() へのラッパー（フィルタあり））
   * @param primaryKeyValue
   * @param filter
   * @param timestamp
   */
  public async getByTimeWithFilter(primaryKeyValue: string, filter: { [key: string]: any }, timestamp = 0): Promise<any[]> {
    const candidate = await this._getByTime(primaryKeyValue, timestamp, filter);
    return candidate.filter((ent: any) => Object.keys(filter).every((k) => ent[k] === filter[k]));
  }

  /**
   * 指定時刻より後に更新されたデータを取得する（指定時刻の情報は含まないことに注意）
   *
   * このオブジェクトが管理している最新の更新データより後に更新された情報をサーバからとってくる
   * timestampを省略した場合は、全部のデータを返す
   * filterを指定した場合は、サーバ側でそこで指定した値を持つものだけフィルタして返す
   * @param primaryKeyValue
   * @param timestamp
   * @param filter
   * @param withDeleted {boolean} trueにすると、deletedフラグのあるものも返答する
   */
  protected async _getByTime(
    primaryKeyValue: string,
    timestamp = 0,
    filter: { [key: string]: any } | undefined = undefined,
    withDeleted = false
  ): Promise<any[]> {
    let res: any;
    // OBJ: primaryKeyValueごとに排他処理
    await this.lock.acquire(primaryKeyValue, async () => {
      const requestedTimestamp = new Date().getTime();
      // OBJ: 1秒以内の頻繁アクセスに対してはサーバへのアクセスを禁止する
      if (this.recentAccess[primaryKeyValue] && requestedTimestamp - this.recentAccess[primaryKeyValue] <= COOLDOWN_TIME) return;
      this.recentAccess[primaryKeyValue] = requestedTimestamp;
      res = await this.getNewRecordsFromServer(primaryKeyValue, timestamp, filter);
      await this.dataCache.loadInMemory(primaryKeyValue); // メモリにprimaryKeyValueのエントリ群を読み出しておく
      if (this.gsiKeyName !== '') {
        let primaryKeys: string[] = res.map((item) => item[this.primaryKeyName]);
        primaryKeys = [...new Set(primaryKeys)].filter((key) => key !== primaryKeyValue);
        await Promise.all(primaryKeys.map((key) => this.dataCache.loadInMemory(key))); // GSIの場合はそれぞれの
      }
    });

    let candidates = this.dataCache.getSortedArray(primaryKeyValue);
    if (this.gsiKeyName) candidates = [...candidates, ...this.dataCache.getSortedArrayOfGsi(primaryKeyValue, this.gsiKeyName)];

    if (withDeleted) {
      // EXP: 指定時刻(timestamp)より後に更新された情報を全て返す(deleted,removeAtなものも含めて全て)
      return JSON.parse(JSON.stringify(candidates.filter((ent: any) => ent.updatedAt > timestamp))); // TODO: structuredCloneに変更する
    } else {
      // EXP: 指定時刻(timestamp)より後に更新された情報かつdeleteフラグが立っていないものだけを返す
      return JSON.parse(
        JSON.stringify(candidates.filter((ent: any) => !ent.deleted && !this.shouldBeRemoved(ent) && ent.updatedAt > timestamp))
      ); // TODO: structuredCloneに変更する
    }
  }

  /**
   *
   * @param primaryKeyValue
   * @param secondaryKeyValue
   */
  public async get(primaryKeyValue: string, secondaryKeyValue: string): Promise<any> {
    await this.getByTime(primaryKeyValue, this.latest[primaryKeyValue]);
    return await this.dataCache.getInMemory(primaryKeyValue, secondaryKeyValue);
  }

  /**
   * DynamoDBから全件取得してIndexedDBに書き込む
   *   件数が多いと時間がかかりキャパシティユニットを大量消費するのでなるべく使わないように
   */
  public async scanAll() {
    // EXP: 全てのエントリーを消してから、サーバから全取得してIndexedDBに書き込む
    for (const key in this.latest) {
      delete this.latest[key];
    }
    this.db.entries.clear();
    this.db.meta.clear();
    this.dataCache = new InMemoryCache(this.primaryKeyName, this.secondaryKeyName, this.getFromIndexedDb); // インメモリキャッシュは作り直す

    const retDatB64 = await this.readAllAdapter();
    if (!retDatB64) return;
    const result: any[] = JSON.parse(pako.inflate(Base64.toUint8Array(retDatB64), { to: 'string' }));
    await this.db.entries.bulkPut(result);
  }

  /**
   * deletedフラグがtrueになっているもの、および1ヶ月以上アクセスされていないメディア情報をローカルのIndexedDBから削除する
   */
  public async purge() {
    const now = new Date().getTime();

    const promises: any = [];

    // EXP: エントリの中のdataに、そのprimaryKeyに属する全てのレコードが含まれているので、dataの中身をひとつずつ全てチェックする
    const allEntries = await this.db.entries.toArray();
    allEntries.forEach((record) => {
      if (!record.deleted && !this.shouldBeRemoved(record)) return;

      this.dataCache.deleteInMemory(record[this.primaryKeyName], record[this.secondaryKeyName]);
      promises.push(this.db.entries.delete([record[this.primaryKeyName], record[this.secondaryKeyName]]));
    });
  }

  /**
   * ローカルストレージのこのテーブルの項目を削除する。これを実行したらオブジェクトが実質的に無効になるので注意。
   */
  public async flush() {
    this.latest = {};
    if (this.initializer) {
      await this.db.entries.clear();
      await this.db.db.delete();
      const db = this.initializer(this.tableName, this._primaryKeyName, this._secondaryKeyName);
      this.db.db = db.db;
      this.db.meta = db.meta;
      this.db.entries = db.entries;
    } else {
      await this.db.entries.clear();
    }
    this.dataCache = new InMemoryCache(this.primaryKeyName, this.secondaryKeyName, this.getFromIndexedDb); // インメモリキャッシュは作り直す
    await this.db.meta.put({ name: 'latest', value: {} });
  }

  /**
   * テスト用の全件取得関数
   */
  public async rawGet() {
    return this.db.entries.toArray();
  }

  /**
   * テスト用の関数（dev環境でだけLambdaが受け付ける）
   */
  public async flushForTesting() {
    await this.flushForTestingAdapter();
  }
}

export { CacheManage };
